import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { findDOMNode } from 'react-dom'
import { DragSource, DropTarget } from 'react-dnd'
import classnames from 'classnames'
import Moment from 'moment'
import momentDurationFormatSetup from 'moment-duration-format'
import ImmutableProptypes from 'react-immutable-proptypes'
import { connect as connectRedux } from 'react-redux'
import Dropdown from 'rc-dropdown'
import Menu, { Item as MenuItem, Divider } from 'rc-menu'
import 'rc-dropdown/assets/index.css'

import Icon from '_components/icon'

import styles from './styles.css'

momentDurationFormatSetup(Moment)

const cardSource = {
  beginDrag(props) {
    return {
      id: props.id,
      index: props.index,
    }
  },
  // eslint-disable-next-line no-unused-vars
  endDrag(props, monitor) {
    props.saveOrdering()
  },
}

const cardTarget = {
  hover(props, monitor, component) {
    const dragIndex = monitor.getItem().index
    const hoverIndex = props.index

    // Don't replace items with themselves
    if (dragIndex === hoverIndex) {
      return
    }

    // Determine rectangle on screen
    const hoverBoundingRect = findDOMNode(component).getBoundingClientRect() // eslint-disable-line react/no-find-dom-node

    // Get vertical middle
    const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

    // Determine mouse position
    const clientOffset = monitor.getClientOffset()

    // Get pixels to the top
    const hoverClientY = clientOffset.y - hoverBoundingRect.top

    // Only perform the move when the mouse has crossed half of the items height
    // When dragging downwards, only move when the cursor is below 50%
    // When dragging upwards, only move when the cursor is above 50%

    // Dragging downwards
    if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
      return
    }

    // Dragging upwards
    if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
      return
    }

    // Time to actually perform the action
    props.moveCard(dragIndex, hoverIndex)

    // Note: we're mutating the monitor item here!
    // Generally it's better to avoid mutations,
    // but it's good here for the sake of performance
    // to avoid expensive index searches.
    monitor.getItem().index = hoverIndex // eslint-disable-line no-param-reassign
  },
}

const mapStateToProps = ({ counter, currentTask, settings }) => ({
  counter,
  currentTask,
  settings,
})

@DropTarget('card', cardTarget, connect => ({
  connectDropTarget: connect.dropTarget(),
}))
@DragSource('card', cardSource, (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  isDragging: monitor.isDragging(),
}))
class OrderingCard extends PureComponent {
  static propTypes = {
    connectDragSource: PropTypes.func.isRequired,
    connectDropTarget: PropTypes.func.isRequired,
    isDragging: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    status: PropTypes.number,
    date: PropTypes.string,
    deleteCard: PropTypes.func.isRequired,
    editCard: PropTypes.func.isRequired,
    playCard: PropTypes.func.isRequired,
    checkCard: PropTypes.func.isRequired,
    shiftCard: PropTypes.func.isRequired,
    counter: ImmutableProptypes.map,
    currentTask: ImmutableProptypes.map,
    createTomorrowItemCard: PropTypes.func.isRequired,
    createCalendarCard: PropTypes.func.isRequired,
  }

  static defaultProps = {
    name: '',
    status: 1,
    currentTask: new Map(),
    counter: new Map(),
    settings: new Map(),
  }

  onDeleteClick = () => {
    const { deleteCard, id } = this.props
    deleteCard(id)
  }

  onEditClick = () => {
    const { editCard, id } = this.props
    editCard(id)
  }

  onPlayClick = () => {
    const { playCard, id } = this.props
    playCard(id)
  }

  onStatusClick = () => {
    const { checkCard, id } = this.props
    checkCard(id)
  }

  onShiftClick = () => {
    const { shiftCard, id } = this.props
    shiftCard(id)
  }

  onCreateTomorrowItemClick = sectionId => {
    const { createTomorrowItemCard, id } = this.props
    createTomorrowItemCard(id, sectionId)
  }

  onCreateCalendarClick = () => {
    const { createCalendarCard, name } = this.props
    createCalendarCard({ name })
  }

  renderStatus = playButtonDisabled => {
    const { status } = this.props
    let iconName = ''

    switch (status) {
      case 0:
        iconName = 'hmm-icon-square-empty'
        break
      case 1:
        iconName = 'hmm-icon-square-partial'
        break
      case 2:
        iconName = 'hmm-icon-square-check'
        break
      case 3:
        iconName = 'hmm-icon-square-partial'
        break
      case 4:
        iconName = 'hmm-icon-square-partial'
        break
      default:
        iconName = 'hmm-icon-square-empty'
    }

    return (
      <button className={classnames(styles.button)} onClick={this.onStatusClick}>
        <i className={classnames(`${iconName}`)} />
      </button>
    )
  }

  renderPlay = playButtonDisabled => {
    const { status, date } = this.props
    if (status !== 2 && new Date(date).toDateString() === new Date().toDateString()) {
      return (
        <button
          className={classnames(styles.button)}
          disabled={playButtonDisabled}
          onClick={this.onPlayClick}
        >
          <i className={classnames('fa fa-play', [playButtonDisabled && styles.disabledButton])} />
        </button>
      )
    }
  }

  handleClick = ({ key }) => {
    switch (key) {
      case '1':
        this.onDeleteClick()
        break
      case '2':
        this.onCreateTomorrowItemClick(0)
        break
      case '3':
        this.onCreateTomorrowItemClick(100)
        break
      case '4':
        this.onCreateTomorrowItemClick(200)
        break
      case '5':
        this.onCreateTomorrowItemClick(300)
        break
      case '6':
        this.onCreateTomorrowItemClick(400)
        break
      case '7':
        this.onCreateCalendarClick()
        break
      default:
    }
    return null
  }

  menu = () => (
    <Menu onClick={this.handleClick} selectable={false}>
      <MenuItem key="1">Delete</MenuItem>
      <MenuItem key="2">Move to Recurring</MenuItem>
      <MenuItem key="3">
        Move to {this.props.settings.get('tomorrowSection1') || 'Tomorrow'}
      </MenuItem>
      <MenuItem key="4">Move to {this.props.settings.get('tomorrowSection2') || 'Later'}</MenuItem>
      <MenuItem key="5">
        Move to {this.props.settings.get('tomorrowSection3') || 'Much Later'}
      </MenuItem>
      <MenuItem key="6">Move to {this.props.settings.get('tomorrowSection4') || 'Never'}</MenuItem>
      <MenuItem key="7">Add to Calendar</MenuItem>
    </Menu>
  )

  render() {
    const {
      isDragging,
      connectDragSource,
      connectDropTarget,
      counter,
      name,
      status,
      date,
    } = this.props
    const playButtonDisabled = counter.get('status') === 'in progress'
    const opacity = isDragging ? 0 : 1

    return Moment(date).format('YYYY-MM-DD') === Moment().format('YYYY-MM-DD') && status !== 2 ? (
      connectDragSource(
        connectDropTarget(
          <div className={classnames(styles.cardContainer)} style={{ opacity }}>
            {/* handle */}
            <div className={classnames(styles.ellipsis)}>
              <i className="fa fa-ellipsis-v" />
              <i className="fa fa-ellipsis-v" style={{ marginLeft: 2 }} />
            </div>
            {/* status */}
            {this.renderStatus(playButtonDisabled)}
            {/* name */}
            <div
              className={classnames(styles.name)}
              onClick={status !== 2 ? this.onEditClick : null}
            >
              {name}
            </div>
            {/* play */}
            {this.renderPlay(playButtonDisabled)}
            {/* menu */}
            {status !== 2 && (
              <Dropdown
                trigger={['click']}
                overlay={this.menu()}
                animation="slide-up"
                className={classnames(styles.button)}
              >
                <button>
                  <i className={classnames('fa fa-ellipsis-v')} />
                </button>
              </Dropdown>
            )}
          </div>
        )
      )
    ) : (
      <div className={classnames(styles.cardContainer)} style={{ opacity }}>
        {this.renderStatus(playButtonDisabled)}
        <div className={classnames(styles.name)}>{name}</div>
      </div>
    )
  }
}

export default connectRedux(mapStateToProps, null)(OrderingCard)



// WEBPACK FOOTER //
// ./src/views/today-items/ordering-card/index.js